// Build-from-Documents batch upload (s98, slice 2). Framework-free orchestration +
// helpers so the sequential-processing state machine and the Populate-All target
// computation are unit-testable with injected deps (no React, no network).
// Design: docs/archive/cc-prompts/build-from-documents.md.

import type { FanOutSection } from "@/components/FanOutBanner";

/** Per-file lifecycle in the batch modal. */
export type BatchFileStatus = "queued" | "processing" | "success" | "failed" | "unclassified";

export interface BatchFileState {
  id: string;
  name: string;
  status: BatchFileStatus;
  jobId?: string;
  /** Gemini classification key (will / insurance_policy / …) when known. */
  documentType?: string;
  /** Fan-out sections this document would populate. */
  sections?: FanOutSection[];
  error?: string;
}

/** Terminal job outcome the poller hands back (status + extraction result fields). */
export interface JobOutcome {
  status: "completed" | "failed";
  documentType?: string;
  fanout?: FanOutSection[];
}

/** Human label for a Gemini documentType key. Unknown / "other" → generic "Document". */
/** Exported for the DOCUMENT_TYPES parity guard (s220 Lane F-b): the client cannot import
 *  `server/document-analysis.ts` (it pulls the Gemini SDK), so a test compares the keys. */
export const DOC_TYPE_LABELS: Record<string, string> = {
  will: "Will",
  insurance_policy: "Insurance policy",
  bank_statement: "Bank statement",
  credit_card_statement: "Credit card statement",
  lease: "Lease",
  property_deed: "Property deed",
  government_id: "Government ID",
  tax_return: "Tax return",
  // s217 types (s220 Lane F-b) — keys must stay in step with DOCUMENT_TYPES; guarded by
  // server/__tests__/document-type-list-parity.test.ts.
  power_of_attorney: "Power of attorney",
  business_agreement: "Business agreement",
  prepaid_funeral_contract: "Prepaid funeral contract",
  other: "Document",
};

export function documentTypeLabel(type: string | undefined | null): string {
  if (!type) return "Document";
  return DOC_TYPE_LABELS[type] ?? "Document";
}

/**
 * Map a terminal job outcome to the file's display state. A failed job → "failed". A
 * completed job with no fan-out AND no real classification → "unclassified" (couldn't
 * identify). Otherwise "success", carrying the type + sections for the summary/populate.
 */
export function deriveFileOutcome(o: JobOutcome, jobId: string): Partial<BatchFileState> {
  if (o.status === "failed") return { status: "failed", jobId };
  const sections = o.fanout ?? [];
  const classified = !!o.documentType && o.documentType !== "other";
  if (sections.length === 0 && !classified) return { status: "unclassified", jobId, sections: [] };
  return { status: "success", jobId, documentType: o.documentType ?? "other", sections };
}

/** Total fan-out sections across all successfully-processed files. */
export function totalSections(files: BatchFileState[]): number {
  return files.reduce((n, f) => n + (f.status === "success" ? (f.sections?.length ?? 0) : 0), 0);
}

export interface PopulateTarget {
  jobId: string;
  sectionId: string;
  sectionName: string;
}

/** Flatten every (jobId, section) pair to populate across all successful files (5A). */
export function collectPopulateTargets(files: BatchFileState[]): PopulateTarget[] {
  const targets: PopulateTarget[] = [];
  for (const f of files) {
    if (f.status !== "success" || !f.jobId) continue;
    for (const s of f.sections ?? []) {
      targets.push({ jobId: f.jobId, sectionId: s.sectionId, sectionName: s.sectionName });
    }
  }
  return targets;
}

// ── 5B Review & Choose selection model ──────────────────────────────────────────
// Per successful file → per section → the selected row indexes. Arrays (not Sets) so
// React state updates immutably and the helpers stay trivially testable.
export type BatchSelection = Record<string, Record<string, number[]>>;

/** Every section + every row checked, for each successfully-processed file. */
export function initBatchSelection(files: BatchFileState[]): BatchSelection {
  const sel: BatchSelection = {};
  for (const f of files) {
    if (f.status !== "success") continue;
    sel[f.id] = {};
    for (const s of f.sections ?? []) sel[f.id][s.sectionId] = (s.rows ?? []).map((_, i) => i);
  }
  return sel;
}

export interface SelectedPopulateTarget {
  jobId: string;
  sectionId: string;
  rowIndexes: number[];
}

/** Flatten the review selection to populate calls — only sections with ≥1 checked row. */
export function collectSelectedPopulateTargets(files: BatchFileState[], selection: BatchSelection): SelectedPopulateTarget[] {
  const targets: SelectedPopulateTarget[] = [];
  for (const f of files) {
    if (f.status !== "success" || !f.jobId) continue;
    const fileSel = selection[f.id] ?? {};
    for (const s of f.sections ?? []) {
      const idx = (fileSel[s.sectionId] ?? []).slice().sort((a, b) => a - b);
      if (idx.length > 0) targets.push({ jobId: f.jobId, sectionId: s.sectionId, rowIndexes: idx });
    }
  }
  return targets;
}

export interface BatchDeps {
  /** Upload a file + enqueue its extraction job; resolves to the jobId. */
  uploadAndExtract: (file: File) => Promise<string>;
  /** Poll the job until it reaches a terminal state, then resolve its outcome. */
  pollJobUntilDone: (jobId: string) => Promise<JobOutcome>;
}

/**
 * Process files ONE AT A TIME (Gemini rate-limit safety): upload → poll → record
 * outcome, emitting state patches via `onUpdate` so the modal renders progressive
 * completion. A per-file failure is captured on that file and never aborts the batch.
 */
export async function runBatchProcessing(
  files: Array<{ id: string; file: File }>,
  deps: BatchDeps,
  onUpdate: (id: string, patch: Partial<BatchFileState>) => void,
): Promise<void> {
  for (const f of files) {
    onUpdate(f.id, { status: "processing" });
    try {
      const jobId = await deps.uploadAndExtract(f.file);
      onUpdate(f.id, { jobId });
      const outcome = await deps.pollJobUntilDone(jobId);
      onUpdate(f.id, deriveFileOutcome(outcome, jobId));
    } catch (e) {
      onUpdate(f.id, { status: "failed", error: e instanceof Error ? e.message : String(e) });
    }
  }
}
